import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Contact, Search, Plus, X, Loader2, Building2, User } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Reusable customer picker. Renders a compact button that opens a search-
 * as-you-type popover. On pick, calls `onPick(customer)` with the full
 * customer object so the parent can hydrate name/email/mobile/etc and
 * store the linkage (customer_id) so history auto-populates.
 */
export default function CustomerPicker({ value, onPick, onClear, testid = "customer-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef = useRef();

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { q, limit: 20 } });
        setResults(Array.isArray(data) ? data : []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debRef.current);
  }, [q, open]);

  const label = value
    ? (value.customer_type === "corporate" && value.company_name
        ? value.company_name
        : [value.title, value.first_name, value.last_name].filter(Boolean).join(" ") || value.email || value.mobile)
    : "";

  if (value) {
    return (
      <div data-testid={`${testid}-attached`} className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-200 rounded px-3 py-2 text-sm">
        <Contact size={14} className="text-emerald-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-slate-900 font-medium truncate">{label}</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-2 truncate">
            <span className="font-mono-e">{value.customer_id}</span>
            {value.email && <span>· {value.email}</span>}
            {value.mobile && <span>· {value.mobile}</span>}
          </div>
        </div>
        <Link to={`/customers/${value.id}`} target="_blank"
              className="text-[11px] text-emerald-700 hover:underline whitespace-nowrap">Open profile</Link>
        {onClear && (
          <button type="button" data-testid={`${testid}-clear`} onClick={onClear}
                  className="text-slate-400 hover:text-red-500 p-1"><X size={13} /></button>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
                className="w-full border border-dashed border-slate-300 hover:border-emerald-400 rounded px-3 py-2 text-sm text-left flex items-center gap-2 bg-white text-slate-500 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
          <Search size={13} className="shrink-0" />
          <span className="flex-1 truncate">Attach an existing customer or create a new one…</span>
          <Contact size={14} className="text-emerald-600 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[420px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
        <div className="p-2 border-b border-slate-100">
          <input autoFocus data-testid={`${testid}-input`} value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, mobile, email, company, CID…"
                 className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {loading && <div className="px-3 py-3 text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-400">No customers found.</div>
          )}
          {results.map((c) => {
            const nm = c.customer_type === "corporate" && c.company_name
              ? c.company_name
              : [c.title, c.first_name, c.last_name].filter(Boolean).join(" ");
            return (
              <button key={c.id} type="button" data-testid={`${testid}-option-${c.id}`}
                      onClick={() => { onPick(c); setOpen(false); setQ(""); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 border-b border-slate-50 last:border-b-0 flex items-start gap-2.5">
                <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${c.customer_type === "corporate" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                  {c.customer_type === "corporate" ? <Building2 size={13} /> : <User size={13} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 truncate font-medium">{nm || c.email || "Unnamed"}</div>
                  <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 truncate">
                    <span className="font-mono-e text-slate-700">{c.customer_id}</span>
                    {c.mobile && <span>· {c.mobile}</span>}
                    {c.email && <span className="truncate">· {c.email}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <span className="text-[10px] text-slate-400">Not here?</span>
          <Link to="/customers/new" target="_blank" data-testid={`${testid}-create-new`}
                className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1">
            <Plus size={11} /> Create new customer
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
